import React, { useState, useEffect, useCallback, useRef } from 'react';

const extractVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|embed\/|watch\?v=|watch\?.+&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
};

const ImageCarousel = ({ product }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMedia, setModalMedia] = useState(null);
    const [itemsPerView, setItemsPerView] = useState(1);
    const scrollContainerRef = useRef(null);

    // Parse media
    const images = product?.images?.split(',').map(url => url.trim()).filter(Boolean) || [];
    const videos = product?.videos?.split(',').map(url => url.trim()).filter(Boolean) || [];
    const mediaItems = [
        ...videos.map((vid) => ({ type: 'video', src: vid })),
        ...images.map((img) => ({ type: 'image', src: img }))
    ];

    // Calculate items per view based on viewport
    useEffect(() => {
        const updateItemsPerView = () => {
            if (window.innerWidth >= 1024) {
                setItemsPerView(3);
            } else if (window.innerWidth >= 640) {
                setItemsPerView(2);
            } else {
                setItemsPerView(1);
            }
        };

        updateItemsPerView();
        window.addEventListener('resize', updateItemsPerView);
        return () => window.removeEventListener('resize', updateItemsPerView);
    }, []);

    // Calculate total pages
    const totalPages = Math.ceil(mediaItems.length / itemsPerView);

    // Navigation handlers
    const goToPage = useCallback((pageIndex) => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const pageWidth = container.offsetWidth;
            container.scrollTo({
                left: pageWidth * pageIndex,
                behavior: 'smooth'
            });
            setCurrentIndex(pageIndex);
        }
    }, []);

    const nextSlide = useCallback(() => {
        if (currentIndex < totalPages - 1) {
            goToPage(currentIndex + 1);
        }
    }, [currentIndex, totalPages, goToPage]);

    const prevSlide = useCallback(() => {
        if (currentIndex > 0) {
            goToPage(currentIndex - 1);
        }
    }, [currentIndex, goToPage]);

    // Handle scroll snap
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const scrollLeft = container.scrollLeft;
            const pageWidth = container.offsetWidth;
            const newIndex = Math.round(scrollLeft / pageWidth);
            setCurrentIndex(newIndex);
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (isModalOpen) return;
            if (e.key === 'ArrowLeft') prevSlide();
            if (e.key === 'ArrowRight') nextSlide();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isModalOpen, prevSlide, nextSlide]);

    // Modal handlers
    const openModal = useCallback((media) => {
        setModalMedia(media);
        setIsModalOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
        setModalMedia(null);
    }, []);

    // Modal escape key and scroll lock
    useEffect(() => {
        if (!isModalOpen) return;

        const handleEscape = (e) => {
            if (e.key === 'Escape') closeModal();
        };

        document.addEventListener('keydown', handleEscape);
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isModalOpen, closeModal]);

    if (mediaItems.length === 0) return null;

    return (
        <div className="md:mt-5 w-full">
            <div className="relative">
                {/* Carousel Container */}
                <div
                    ref={scrollContainerRef}
                    className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 px-2"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                    {mediaItems.map((media, index) => (
                        <div
                            key={index}
                            className="flex-shrink-0 snap-start w-full md:w-[calc(50%-0.5rem)] xl:w-[calc(33.333%-0.667rem)]"
                        >
                            <div
                                className="flex items-center justify-center cursor-pointer h-full"
                                onClick={() => openModal(media)}
                            >
                                {media.type === 'image' ? (
                                    <img
                                        src={media.src}
                                        alt={`Product ${index + 1}`}
                                        className="w-full h-64 md:h-72 xl:h-64 object-contain rounded-lg bg-gray-100"
                                        loading="lazy"
                                    />
                                ) : (
                                    <iframe
                                        className="w-full h-64 md:h-72 xl:h-64 rounded-lg"
                                        src={`https://www.youtube.com/embed/${extractVideoId(media.src)}`}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                        title={`Product Video ${index + 1}`}
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Navigation Arrows */}
                {totalPages > 1 && (
                    <>
                        <button
                            onClick={prevSlide}
                            disabled={currentIndex === 0}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed rounded-full p-2 shadow-lg transition-all z-10"
                            aria-label="Previous slide"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <button
                            onClick={nextSlide}
                            disabled={currentIndex === totalPages - 1}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed rounded-full p-2 shadow-lg transition-all z-10"
                            aria-label="Next slide"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </>
                )}

                {/* Indicators */}
                {totalPages > 1 && (
                    <div className="flex justify-center gap-2 mt-4">
                        {Array.from({ length: totalPages }).map((_, index) => (
                            <button
                                key={index}
                                onClick={() => goToPage(index)}
                                className={`h-2 rounded-full transition-all ${index === currentIndex
                                        ? 'w-8 bg-blue-600'
                                        : 'w-2 bg-gray-300 hover:bg-gray-400'
                                    }`}
                                aria-label={`Go to page ${index + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Modal */}
            {isModalOpen && modalMedia && (
                <div
                    className="fixed inset-0 bg-white flex items-center justify-center z-50 p-4"
                    onClick={closeModal}
                    role="dialog"
                    aria-modal="true"
                >
                    <button
                        onClick={closeModal}
                        className="absolute top-4 right-4 text-black text-3xl font-bold hover:text-gray-300 transition-colors z-10"
                        aria-label="Close modal"
                    >
                        ×
                    </button>

                    <div onClick={(e) => e.stopPropagation()}>
                        {modalMedia.type === 'image' ? (
                            <img
                                src={modalMedia.src}
                                alt="Fullscreen"
                                className="max-w-full max-h-[90vh] object-contain"
                            />
                        ) : (
                            <iframe
                                className="max-w-full max-h-[90vh] aspect-video"
                                src={`https://www.youtube.com/embed/${extractVideoId(modalMedia.src)}?autoplay=1`}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title="Fullscreen Video"
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImageCarousel;